/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { resolveVideoUrl } from '@sportninja/common/utils/assetUrl'
import { Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import Text from 'src/components/ts-components/Text'
import VideoPlayer from 'src/components/HighlightsV2/player/VideoPlayer'
import PlayerHighlightCard from '../../PlayerHighlightCard'
import SectionHeader from './SectionHeader'
import { PlayerHighlight } from '../../types'

interface CareerHighlightsWidgetProps {
  highlights: PlayerHighlight[]
  onViewAll: () => void
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
  },
}

const CareerHighlightsWidget = ({
  highlights,
  onViewAll,
}: CareerHighlightsWidgetProps) => {
  const [selectedHighlight, setSelectedHighlight] =
    useState<PlayerHighlight | null>(null)

  const displayHighlights = highlights.slice(0, 4)

  const handleCardClick = (highlight: any) => {
    if (highlight.video_url) {
      setSelectedHighlight(highlight)
    }
  }

  return (
    <div
      css={css`
        background-color: ${colors.SECONDARY_400};
        border-radius: 8px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <SectionHeader title='Career Highlights' onViewAll={onViewAll} />

      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;

          @media (max-width: 1024px) {
            grid-template-columns: repeat(2, 1fr);
          }

          @media (max-width: 600px) {
            grid-template-columns: 1fr;
          }
        `}
      >
        {displayHighlights.map((highlight) => (
          <PlayerHighlightCard
            key={highlight.id}
            highlight={highlight}
            onClick={handleCardClick}
            isPlaying={selectedHighlight?.id === highlight.id}
          />
        ))}
      </div>

      <Modal
        open={!!selectedHighlight}
        onCancel={() => setSelectedHighlight(null)}
        footer={null}
        width={800}
        destroyOnClose
        centered
        title={
          <div
            css={css`
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
              {selectedHighlight?.title || ''}
            </Text>
          </div>
        }
        closeIcon={
          <CloseOutlined
            css={css`
              color: ${colors.SOLITUDE};
              font-size: 16px;
            `}
          />
        }
        styles={modalStyles}
      >
        <VideoPlayer url={resolveVideoUrl(selectedHighlight?.video_url)} />
      </Modal>
    </div>
  )
}

export default CareerHighlightsWidget
